import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Layout,
  Button,
  Row,
  Col,
  Typography,
  Form,
  Input,
  Alert,
  Card,
  Spin,
} from "antd";
import { LockOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { userService } from "../../services/userService";

import "./PasswordResetForm.css";

const { Title, Paragraph } = Typography;
const { Content } = Layout;

const PasswordResetForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setError("Invalid or missing reset token");
        setValidating(false);
        return;
      }

      try {
        const response = await userService.validatePasswordResetToken(token);
        if (response.valid) {
          setTokenValid(true);
        } else {
          setError("This password reset link is invalid or has expired");
        }
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            "This password reset link is invalid or has expired"
        );
      } finally {
        setValidating(false);
      }
    };

    validateToken();
  }, [token]);

  const onFinish = async (values) => {
    setLoading(true);
    setError(null);

    try {
      await userService.resetPassword(token, values.newPassword);
      setSuccess(true);
      form.resetFields();

      // Redirect to sign-in after 3 seconds
      setTimeout(() => {
        navigate("/sign-in");
      }, 3000);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to reset password. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log("Failed:", errorInfo);
  };

  if (validating) {
    return (
      <Layout className="layout-default layout-password-reset-form">
        <Content className="password-reset-form-page">
          <Row gutter={[24, 0]} justify="center">
            <Col xs={{ span: 24 }} lg={{ span: 8 }} md={{ span: 12 }}>
              <Card className="password-reset-form-card">
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <Spin size="large" />
                  <div style={{ marginTop: 16 }}>Validating reset link...</div>
                </div>
              </Card>
            </Col>
          </Row>
        </Content>
      </Layout>
    );
  }

  if (!tokenValid && !success) {
    return (
      <Layout className="layout-default layout-password-reset-form">
        <Content className="password-reset-form-page">
          <Card className="password-reset-form-card">
            <Alert
              message="Invalid Reset Link"
              description={
                error ||
                "This password reset link is invalid or has expired. Please request a new one."
              }
              type="error"
              showIcon
              style={{ marginBottom: 24 }}
            />
            <Button
              type="primary"
              onClick={() => navigate("/password-reset-request")}
              style={{ width: "100%" }}
              size="large"
            >
              Request New Reset Link
            </Button>
          </Card>
        </Content>
      </Layout>
    );
  }

  return (
    <Layout className="layout-default layout-password-reset-form">
      <Content className="password-reset-form-page">
        <Card className="password-reset-form-card">
          {success ? (
            <>
              <div style={{ textAlign: "center" }}>
                <CheckCircleOutlined
                  style={{
                    fontSize: 72,
                    color: "#52c41a",
                    marginBottom: 24,
                  }}
                />
              </div>
              <Alert
                message="Password Reset Successful!"
                description="Your password has been reset successfully. Redirecting to sign in page..."
                type="success"
                showIcon
                style={{ marginBottom: 24 }}
              />
            </>
          ) : (
            <>
              <Title className="mb-15" level={2}>
                Set New Password
              </Title>
              <Paragraph className="text-muted mb-24">
                Enter your new password below.
              </Paragraph>

              {/* Error Message */}
              {error && (
                <Alert
                  message="Reset Failed"
                  description={error}
                  type="error"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}

              <Form
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                layout="vertical"
                className="password-reset-form"
                form={form}
              >
                <Form.Item
                  label="New Password"
                  name="newPassword"
                  rules={[
                    {
                      required: true,
                      message: "Please input your new password!",
                    },
                    {
                      min: 8,
                      message: "Password must be at least 8 characters!",
                    },
                  ]}
                  hasFeedback
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Enter new password"
                    size="large"
                  />
                </Form.Item>

                <Form.Item
                  label="Confirm Password"
                  name="confirmPassword"
                  dependencies={["newPassword"]}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: "Please confirm your password!",
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("newPassword") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("The two passwords do not match!")
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="Confirm new password"
                    size="large"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{ width: "100%" }}
                    size="large"
                    loading={loading}
                  >
                    Reset Password
                  </Button>
                </Form.Item>
              </Form>
            </>
          )}
        </Card>
      </Content>
    </Layout>
  );
};

export default PasswordResetForm;
